import { DocumentData, DocumentReference } from 'firebase/firestore';

export interface StoreLocation {
  latitude: number;
  longitude: number;
}

export type OnlineOrderControlMode = 'FIRST_PREPARE_WINS' | 'POSCAISSE_ONLY';
export type ProductAvailabilityControlMode = 'STEP_STORE' | 'POSCAISSE';
export type MenuCatalogOwner = 'ADMIN_APP' | 'POSCAISSE';

export const DEFAULT_ONLINE_ORDER_CONTROL_MODE: OnlineOrderControlMode =
  'FIRST_PREPARE_WINS';
export const DEFAULT_PRODUCT_AVAILABILITY_CONTROL_MODE: ProductAvailabilityControlMode =
  'STEP_STORE';
export const DEFAULT_MENU_CATALOG_OWNER: MenuCatalogOwner = 'ADMIN_APP';

export const normalizeOnlineOrderControlMode = (
  value: unknown
): OnlineOrderControlMode =>
  value === 'POSCAISSE_ONLY' ? 'POSCAISSE_ONLY' : DEFAULT_ONLINE_ORDER_CONTROL_MODE;

export const normalizeProductAvailabilityControlMode = (
  value: unknown
): ProductAvailabilityControlMode =>
  value === 'POSCAISSE'
    ? 'POSCAISSE'
    : DEFAULT_PRODUCT_AVAILABILITY_CONTROL_MODE;

export const normalizeMenuCatalogOwner = (value: unknown): MenuCatalogOwner =>
  value === 'POSCAISSE' ? 'POSCAISSE' : DEFAULT_MENU_CATALOG_OWNER;

export interface Store {
  id: string;
  name: string;
  address: string;
  location: StoreLocation;
  isActive: boolean;
  isDeliveryResponsible?: boolean;
  operationGroupId?: string | null;
  isVisibleOnline?: boolean;
  isPickupEnabled?: boolean;
  isDeliveryEnabled?: boolean;
  onlineOrderControlMode?: OnlineOrderControlMode;
  productAvailabilityControlMode?: ProductAvailabilityControlMode;
  menuCatalogOwner?: MenuCatalogOwner;
  phone?: string;
  email?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface StoreFormData {
  name: string;
  address: string;
  location: StoreLocation;
  isActive: boolean;
  isDeliveryResponsible?: boolean;
  operationGroupId?: string | null;
  isVisibleOnline?: boolean;
  isPickupEnabled?: boolean;
  isDeliveryEnabled?: boolean;
  onlineOrderControlMode?: OnlineOrderControlMode;
  productAvailabilityControlMode?: ProductAvailabilityControlMode;
  menuCatalogOwner?: MenuCatalogOwner;
  phone?: string;
  email?: string;
}

export const toStore = (
  doc: DocumentReference<DocumentData> | { id: string } & DocumentData
): Store => {
  const data = 'data' in doc ? doc.data() : doc;
  return {
    id: doc.id,
    name: data.name || '',
    address: data.address || '',
    location: data.location || { latitude: 0, longitude: 0 },
    isActive: data.isActive ?? true,
    isDeliveryResponsible: data.isDeliveryResponsible ?? false,
    operationGroupId: data.operationGroupId ?? null,
    isVisibleOnline: data.isVisibleOnline ?? false,
    isPickupEnabled: data.isPickupEnabled ?? true,
    isDeliveryEnabled: data.isDeliveryEnabled ?? true,
    onlineOrderControlMode: normalizeOnlineOrderControlMode(data.onlineOrderControlMode),
    productAvailabilityControlMode: normalizeProductAvailabilityControlMode(
      data.productAvailabilityControlMode
    ),
    menuCatalogOwner: normalizeMenuCatalogOwner(data.menuCatalogOwner),
    phone: data.phone || '',
    email: data.email || '',
    createdAt: data.createdAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
  };
};
